import apiClient, { unwrapData } from "./apiClient";
import { startCall } from "./callsApi";
import { getLeadContextLeads, getLeadContextStatus } from "./leadsApi";

// Build the canonical /api/calls/start request body.
// Flat, single object that matches the backend schema. Only defined values are
// included, so we never send empty strings (which would fail the backend's
// min-length validation on optional fields like ai_config_id / trunk / caller_id).
function buildCallStartBody(payload = {}) {
  const trim = (v) => (v === undefined || v === null ? "" : String(v).trim());

  const phone = trim(payload.phone);
  const personName = trim(payload.name || payload.patient_name);
  const reason = trim(payload.reason) || "follow-up";
  const aiConfigId = trim(payload.ai_config_id);
  const trunk = trim(payload.trunk);
  const callerId = trim(payload.caller_id);
  const leadSource = trim(payload.lead_source || payload.metadata?.lead_source);
  // Voice engine selection. Defaults to OpenAI so the existing flow is unchanged.
  const voiceProvider = trim(payload.voice_provider) || "openai";

  const body = {
    phone: phone || undefined,
    contact_id: payload.contact_id || undefined,
    name: personName || undefined,
    reason,
    ai_config_id: aiConfigId || undefined,
    client_id: payload.client_id || undefined,
    campaign_id: payload.campaign_id || undefined,
    trunk: trunk || undefined,
    caller_id: callerId || undefined,
    timeout_seconds: payload.timeout_seconds || undefined,
    channel_type: "ai",
    voice_provider: voiceProvider,
    metadata: {
      source: payload.metadata?.source || "dashboard",
      ...(leadSource ? { lead_source: leadSource } : {}),
    },
  };

  // Strip undefined keys so the payload stays clean.
  Object.keys(body).forEach((key) => body[key] === undefined && delete body[key]);
  return body;
}

export async function startOutboundCall(payload, options = {}) {
  const data = await startCall(buildCallStartBody(payload), options);

  return {
    provider: "Backend ARI",
    ...data,
  };
}

async function getContactsAsLeads(options = {}) {
  const response = await apiClient.get("/api/contacts", {
    ...options,
    query: { limit: 200, ...(options.query || {}) },
  });
  const data = unwrapData(response) || {};
  const contacts = Array.isArray(data) ? data : data.contacts || data.items || [];

  return contacts
    .filter((c) => c.phone || c.phone_number)
    .map((c) => ({
      id: c.contact_id || c.id,
      full_name: c.name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "Unknown",
      phone: c.phone || c.phone_number || "",
      email: c.email || "",
      lead_score: null,
      pam_task: "follow_up",
      next_best_action: "General follow-up",
      summary_text: c.notes || "",
      _source: "contacts",
    }));
}

export async function getOutboundQueueLeads(options = {}) {
  const status = await getLeadContextStatus(options).catch(() => null);
  const leadContextEnabled = status?.enabled !== false;

  if (leadContextEnabled) {
    const leads = await getLeadContextLeads({
      ...options,
      query: { callable: true, sort: "priority", ...(options.query || {}) },
    });
    const leadList = Array.isArray(leads) ? leads : leads?.leads || [];

    if (leadList.length > 0) {
      return leadList;
    }
  }

  return getContactsAsLeads(options);
}
